/**
 * desktop-map-cache — serve map-bundle files from the Rust disk cache.
 *
 * RESPONSIBILITY: On the desktop, route the map engine's `fetch` of
 * immutable bundle files (footprint, furniture, model manifests on the public
 * assets bucket) through the `cached_get` command in src-tauri/src/lib.rs.
 * First open of a floor is a network round trip as before; every open after
 * that paints from disk while Rust revalidates the ETag in the background.
 *
 * WHY A FETCH PATCH: `@ricoh-maps/angular` is a published package that calls
 * `window.fetch` directly, so the host cannot inject a transport. Patching
 * the global for one origin prefix is the smallest seam. Web is untouched
 * (`installDesktopMapCache` is a no-op outside Tauri).
 *
 * USED BY: main.ts (before bootstrap, so the first map load already benefits).
 */
import { isTauri } from './tauri-runtime';

/** Mirrors `CACHEABLE_PREFIXES` in lib.rs — the command refuses anything else. */
export const CACHEABLE_BUNDLE_PREFIXES: readonly string[] = [
  'https://storage.googleapis.com/ricoh-spaces-public-assets/',
];

export function isCacheableBundleUrl(url: string): boolean {
  return CACHEABLE_BUNDLE_PREFIXES.some((p) => url.startsWith(p));
}

/** The engine reads `.json()` / `.arrayBuffer()`; a sensible type keeps DevTools honest. */
export function guessContentType(url: string): string {
  const path = url.split('?')[0].toLowerCase();
  if (path.endsWith('.json') || path.endsWith('.geojson')) return 'application/json';
  if (path.endsWith('.glb')) return 'model/gltf-binary';
  if (path.endsWith('.gltf')) return 'model/gltf+json';
  if (path.endsWith('.png')) return 'image/png';
  if (path.endsWith('.jpg') || path.endsWith('.jpeg')) return 'image/jpeg';
  if (path.endsWith('.svg')) return 'image/svg+xml';
  if (path.endsWith('.bin') || path.endsWith('.basis') || path.endsWith('.ktx2')) {
    return 'application/octet-stream';
  }
  return 'application/octet-stream';
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

function requestMethod(input: RequestInfo | URL, init?: RequestInit): string {
  return (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
}

let installed = false;

/** Patch `window.fetch` once. Safe to call repeatedly; no-op on the web. */
export function installDesktopMapCache(): void {
  if (!isTauri || installed || typeof window === 'undefined') return;
  installed = true;
  const original = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = requestUrl(input);
    if (requestMethod(input, init) !== 'GET' || !isCacheableBundleUrl(url)) {
      return original(input, init);
    }
    try {
      const { invoke } = await import('@tauri-apps/api/core');
      const bytes = await invoke<ArrayBuffer>('cached_get', { url });
      return new Response(bytes, {
        status: 200,
        headers: { 'content-type': guessContentType(url), 'x-ricoh-cache': 'desktop' },
      });
    } catch (err) {
      // Cache miss that also failed upstream, or the command refused the URL:
      // the network path is still the truth, so fall back to it.
      console.warn('[desktop-map-cache] falling back to network:', err);
      return original(input, init);
    }
  };
}
